// Foundations
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs/operators';

// Services
import { LoadingIndicatorService } from '@app/services/loading-indicator.service';
import { PaymentService } from '@app/services/payment.service';
import { AnalyticsService } from '@app/services/analytics.service';
import { DialogService } from '@app/services/dialog.service';
import { MyProfileService } from '@app/services/my-profile.service';

// Components
import { PaymentDetailsFormComponent } from '@app/components/_share/payment-details-form/payment-details-form.component';

// Models
import { PaymentCreditCard } from '@app/models/payment/payment-card.model';
import { FranchiseLocation } from '@app/models/franchise.model';

@Component({
  selector: 'app-payment-edit',
  templateUrl: './payment-edit.component.html',
  styleUrls: ['./payment-edit.component.scss'],
})
export class PaymentEditComponent implements AfterViewInit {
  // #region -> Property <-
  // ----------

  @ViewChild('paymentDetailsForm', { static: false })
  paymentDetailsFormComponent: PaymentDetailsFormComponent;

  paymentCreditCardId: string;
  paymentCreditCard: PaymentCreditCard;
  isEditMode = false;

  preferredLocationId: string;

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private loadingIndicatorService: LoadingIndicatorService,
    private dialogService: DialogService,
    private paymentService: PaymentService,
    private myProfileService: MyProfileService,
    private _analytics: AnalyticsService
  ) {
    // Get the payment Id - editing mode
    this.paymentCreditCardId = this.route.snapshot.paramMap.get('id');
    if (this.paymentCreditCardId) {
      this.isEditMode = true;
    }
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Lifecycle <-
  // ----------

  ngAfterViewInit(): void {
    setTimeout(() => {
      this._fetchData();
    }, 0);
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method <-
  // ----------

  submittedInSuccess(event) {
    //
    const formGroup: FormGroup = event as FormGroup;

    this.loadingIndicatorService.show();

    if (!this.isEditMode) {
      // Add
      this._analytics.logButtonTap('editPayment_addNewPayment');

      //
      const newCardInfo: PaymentCreditCard = {
        cardToken: formGroup.controls.cardToken.value,
        cardholderName: formGroup.controls.cardholderName.value,
        cardNickname: formGroup.controls.cardNickname.value,
        cardType: formGroup.controls.cardType.value,
        // cardExpirationDate: moment().toDate(),
        isPrimary: !this.paymentService.hasPrimaryPaymentCard(),
      };
      this.paymentService
        .addPaymentCard(newCardInfo, this.preferredLocationId)
        .pipe(finalize(() => this.loadingIndicatorService.hide()))
        .subscribe((paymentCreditCard) => {
          // Show successful message
          this._showSuccessDialog(paymentCreditCard);
        });
    } else {
      // Update
      this._analytics.logButtonTap('editPayment_updatePayment');

      //
      const updatedCardInfo: PaymentCreditCard = {
        id: this.paymentCreditCard.id,
        cardToken: formGroup.controls.cardToken.value,
        cardholderName: formGroup.controls.cardholderName.value,
        cardNickname: formGroup.controls.cardNickname.value,
        cardType: formGroup.controls.cardType.value,
        cardExpirationDate: this._buildExpiryDate(
          formGroup.controls.expMonth.value,
          formGroup.controls.expYear.value
        ),
        isPrimary: this.paymentCreditCard.isPrimary,
      };
      this.paymentService
        .updatePaymentCard(updatedCardInfo)
        .pipe(finalize(() => this.loadingIndicatorService.hide()))
        .subscribe((paymentCreditCard) => {
          // Show successful message
          this._showSuccessDialog(paymentCreditCard);
        });
    }
  }

  submittedInFailure(event) {
    //
    const message =
      event.toLowerCase().charAt(0).toUpperCase() +
      event.toLowerCase().slice(1);
    this.dialogService.showAlert({ title: 'Error', content: message });
  }

  onSave() {
    this.paymentDetailsFormComponent.submit();
  }

  onCancel() {
    this.router.navigate(['/payments']);
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Private Method <-
  // ----------

  private _fetchPaymentCreditCard(paymentCreditCardId: string) {
    // Get the info from the resolver.
    this.paymentCreditCard = this.route.snapshot.data['paymentCard'];
    this.paymentDetailsFormComponent.patchData(this.paymentCreditCard);
    // this.paymentService
    //   .getPaymentCreditCardById(paymentCreditCardId)
    //   .subscribe((paymentCreditCard) => {
    //     this.paymentCreditCard = paymentCreditCard;
    //     this.paymentDetailsFormComponent.patchData(paymentCreditCard);
    //   });
  }

  private _fetchData() {
    // Get "preferred location" from the resolver.
    const preferredLocation = this.route.snapshot.data[
      'preferredLocation'
    ] as FranchiseLocation;
    this.preferredLocationId = preferredLocation.id;

    // Fetch the payment card info in the "edit mode"
    if (this.isEditMode) {
      //
      this._fetchPaymentCreditCard(this.paymentCreditCardId);
    }
  }

  private _showSuccessDialog(card: PaymentCreditCard) {
    //
    const title = this.isEditMode
      ? 'Credit Card Updated!'
      : 'Credit Card Added!';
    const content = this.isEditMode
      ? `The credit card (${card.cardAccount}) has been updated successfully.`
      : `The credit card (${card.cardAccount}) has been added to your account successfully.`;

    this.dialogService.showAlert({
      title,
      content,
      okCallback: () => {
        this.router.navigate(['../'], { relativeTo: this.route });
      },
    });
  }

  private _buildExpiryDate(month: string, year: string): Date {
    // get date for the last day of the month
    return new Date(Number('20' + year), Number(month), 0);
  }

  // ----------
  // #endregion

  /////////////////////////////
}
